/**
 * Drafts: in-progress work that must survive a reload, a crash or a dead battery — a delivery
 * being weighed bag by bag, a loading list. A draft becomes commands only at commit; until then
 * it lives here (F4.1-h: "drafts kept on the phone until commit or void").
 *
 * `data` is sealed with the device key when the phone has one (sealed.ts); every read here opens
 * it. A draft no key opens (a wiped phone) reads as absent.
 */
import { getDb, type DraftRecord, type SupplyChainDB } from './db'
import { openDraft, openDrafts, sealValue } from './sealed'

export async function saveDraft<T>(
  draft: Omit<DraftRecord<T>, 'created_at' | 'updated_at'> & { created_at?: number },
  db: SupplyChainDB = getDb(),
): Promise<DraftRecord<T>> {
  const now = Date.now()
  const existing = await db.drafts.get(draft.id)
  const rec: DraftRecord<T> = {
    ...draft,
    created_at: existing?.created_at ?? draft.created_at ?? now,
    updated_at: now,
  }
  await db.drafts.put({ ...rec, data: await sealValue(rec.data, db) } as DraftRecord)
  return rec
}

export async function getDraft<T>(
  id: string,
  db: SupplyChainDB = getDb(),
): Promise<DraftRecord<T> | undefined> {
  return openDraft<T>(await db.drafts.get(id), db)
}

export async function listDrafts<T>(
  kind: string,
  db: SupplyChainDB = getDb(),
): Promise<DraftRecord<T>[]> {
  const rows = await db.drafts.where('kind').equals(kind).reverse().sortBy('updated_at')
  return openDrafts<T>(rows, db)
}

export async function deleteDraft(id: string, db: SupplyChainDB = getDb()): Promise<void> {
  await db.drafts.delete(id)
}
